<template>
  <v-dialog v-model="visible" persistent max-width="700" scrollable @click:outside="close">
    <v-card>
      <!-- Header -->
      <v-toolbar color="success darken-2" dark flat>
        <v-toolbar-title class="font-weight-bold text-subtitle-1">
          {{ $t('memberDataSection.greenCoffee.viewTitle') }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-0" style="max-height: calc(90vh - 120px); overflow-y: auto;">
        <!-- Loading state -->
        <div v-if="loading" class="d-flex justify-center align-center pa-10">
          <v-progress-circular indeterminate color="success"></v-progress-circular>
        </div>

        <template v-else-if="detail">
          <!-- Summary Row -->
          <div class="px-6 pt-5 pb-4">
            <v-row>
              <v-col cols="12" sm="4">
                <div class="field-label">{{ $t('memberDataSection.greenCoffee.outturnNumber') }}</div>
                <div class="field-value">{{ detail.outturnNumber }}</div>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="field-label">{{ $t('memberDataSection.greenCoffee.totalQuantity') }}</div>
                <div class="field-value">{{ detail.totalGreenBeans != null ? detail.totalGreenBeans + ' kg' : '—' }}</div>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="field-label">{{ $t('status') }}</div>
                <v-btn x-small rounded min-width="160" depressed class="green-button" height="34"
                  v-if="detail.status == 'Completed'">
                  {{ detail.status || $t('memberDataSection.greenCoffee.complete') }}
                </v-btn>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="field-label">{{ $t('memberDataSection.greenCoffee.startDate') }}</div>
                <div class="field-value">{{ formatDate(detail.purchaseDate) }}</div>
              </v-col>
            </v-row>
          </div>

          <v-divider></v-divider>

          <!-- Parchment Information -->
          <div class="section-header d-flex justify-space-between align-center px-6 py-3" @click="toggleSection('parchment')">
            <span class="section-title">{{ $t('memberDataSection.greenCoffee.parchmentInformation') }}</span>
            <v-icon>{{ sections.parchment ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
          <v-expand-transition>
            <div v-if="sections.parchment">
              <template v-if="detail.batches && detail.batches.length">
                <div v-for="(batch, idx) in detail.batches" :key="idx">
                  <div class="px-6 py-4">
                    <v-row>
                      <v-col cols="12" sm="4">
                        <div class="field-label">{{ $t('memberDataSection.greenCoffee.batchId') }}</div>
                        <div class="field-value">{{ batch.batchId }}</div>
                      </v-col>
                      <v-col cols="12" sm="4">
                        <div class="field-label">{{ $t('memberDataSection.greenCoffee.quantity') }}</div>
                        <div class="field-value">{{ batch.quantity != null ? batch.quantity + ' kg' : '—' }}</div>
                      </v-col>
                      <v-col cols="12" sm="4">
                      <div class="field-label">{{ $t('memberDataSection.greenCoffee.supplier') }}</div>
                      <div class="field-value">{{ batch.supplier }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">{{ $t('memberDataSection.greenCoffee.species') }}</div>
                      <div class="field-value">{{ batch.species }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">{{ $t('memberDataSection.greenCoffee.variety') }}</div>
                      <div class="field-value">{{ batch.variety }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">{{ $t('memberDataSection.greenCoffee.process') }}</div>
                      <div class="field-value">{{ batch.process }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">{{ $t('memberDataSection.greenCoffee.batchRating') }}</div>
                      <div class="field-value">{{ batch.batchRating || '—' }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">{{ $t('memberDataSection.greenCoffee.humidity') }}</div>
                      <div class="field-value">{{ batch.humidity != null ? batch.humidity + ' %' : '—' }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
<div class="field-label">{{ $t('memberDataSection.greenCoffee.density') }}</div>
                        <div class="field-value">{{ batch.density != null ? batch.density + ' kg/cm3' : '—' }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">{{ $t('memberDataSection.greenCoffee.totalCoffeeCherryQty') }}</div>
                      <div class="field-value">{{ batch.totalCoffeeCherryQty != null ? batch.totalCoffeeCherryQty + ' kg' : '—' }}</div>
                    </v-col>
                    </v-row>
                  </div>
                  <v-divider v-if="idx < detail.batches.length - 1"></v-divider>
                </div>
              </template>
              <div v-else class="px-6 py-4 text--secondary text-body-2">{{ $t('memberDataSection.greenCoffee.noParchmentBatches') }}</div>
            </div>
          </v-expand-transition>

          <v-divider></v-divider>

          <!-- Process Information -->
          <div class="section-header d-flex justify-space-between align-center px-6 py-3" @click="toggleSection('process')">
            <span class="section-title">{{ $t('memberDataSection.greenCoffee.processInformation') }}</span>
            <v-icon>{{ sections.process ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
          <v-expand-transition>
            <div v-if="sections.process" class="px-6 py-4">
              <v-row>
                <v-col cols="12" sm="4">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.airHumidity') }}</div>
                  <div class="field-value">{{ detail.airHumidity != null ? detail.airHumidity : '—' }}</div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.density') }}</div>
                  <div class="field-value">{{ detail.density != null ? detail.density + (detail.densityUnit ? ' ' + detail.densityUnit : ' kg/cm3') : '—' }}</div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.batchProductionDensity') }}</div>
                  <div class="field-value">{{ detail.batchProductionDensity != null ? detail.batchProductionDensity + ' kg/cm3' : '—' }}</div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.qualityScore') }}</div>
                  <div class="field-value">{{ detail.qualityScore }}</div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.weightBeforeProcessing') }}</div>
                  <div class="field-value">{{ detail.weightBeforeProcessing != null ? detail.weightBeforeProcessing + ' kg' : '—' }}</div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.percentagePrimaryDefect') }}</div>
                  <div class="field-value">{{ detail.primaryDefect != null ? detail.primaryDefect + '%' : '—' }}</div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.percentageSecondaryDefect') }}</div>
                  <div class="field-value">{{ detail.secondaryDefect != null ? detail.secondaryDefect + '%' : '—' }}</div>
                </v-col>
              </v-row>
            </div>
          </v-expand-transition>

          <v-divider></v-divider>

          <!-- Green Beans Information -->
          <div class="section-header d-flex justify-space-between align-center px-6 py-3" @click="toggleSection('greenBeans')">
            <span class="section-title">{{ $t('memberDataSection.greenCoffee.greenBeansInformation') }}</span>
            <v-icon>{{ sections.greenBeans ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
          <v-expand-transition>
            <div v-if="sections.greenBeans" class="px-6 py-4">
              <v-row>
                <v-col cols="12" sm="4">
<div class="field-label">{{ $t('memberDataSection.greenCoffee.outturnNumber') }}</div>
                <div class="field-value">{{ detail.greenBeansId }}</div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.totalGreenBeans') }}</div>
                  <div class="field-value">{{ detail.totalGreenBeans != null ? detail.totalGreenBeans + ' kg' : '—' }}</div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.numberOfBags') }}</div>
                  <div class="field-value">{{ detail.numberOfBags != null ? detail.numberOfBags : '—' }}</div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.leftoverQuantity') }}</div>
                  <div class="field-value">{{ detail.leftoverQuantity != null ? detail.leftoverQuantity + ' kg' : '—' }}</div>
                </v-col>
                <!-- <v-col cols="12" sm="4">
                  <div class="field-label">Quality Score</div>
                  <div class="field-value">{{ detail.greenBeansLabel }}</div>
                </v-col> -->
                <v-col cols="12" sm="4">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.expiryDate') }}</div>
                  <div class="field-value">{{ formatDate(detail.expiryDate) }}</div>
                </v-col>
              </v-row>
            </div>
          </v-expand-transition>

          <v-divider></v-divider>

          <!-- Traceability Label -->
          <div
            class="section-header d-flex justify-space-between align-center px-6 py-3"
            @click="toggleSection('traceability')"
          >
            <span class="section-title">{{ $t('memberDataSection.greenCoffee.traceabilityLabel') }}</span>
            <v-icon>{{ sections.traceability ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
          <v-expand-transition>
            <div v-if="sections.traceability" class="px-6 py-4">
              <v-row>
                <v-col v-if="detail.qrCodeUrl" cols="12" sm="4">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.qrCode') }}</div>
                  <div class="mt-2">
                    <v-img
                      :src="detail.qrCodeUrl"
                      max-width="120"
                      max-height="120"
                      contain
                    ></v-img>
                  </div>
                  <div class="mt-3">
                    <v-btn
                      color="brown darken-2"
                      class="white--text text-none px-6"
                      @click="downloadQr"
                    >
                      {{ $t('dueDiligence.downloadQrCode') || 'DOWNLOAD QR' }}
                    </v-btn>
                  </div>
                </v-col>
                <v-col cols="12" :sm="detail.qrCodeUrl ? 8 : 12">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.externalId') }}</div>
                  <div class="field-value">{{ detail.externalId || '—' }}</div>
                  <template v-if="detail.labelUrl">
                    <div class="mt-4">
                      <v-img
                        :src="detail.labelUrl"
                        max-width="160"
                        max-height="220"
                        contain
                      ></v-img>
                    </div>
                    <div class="mt-4">
                      <v-btn
                        color="brown darken-2"
                        class="white--text text-none px-6"
                        @click="downloadLabel(detail.labelUrl)"
                      >
                        {{ $t('memberDataSection.greenCoffee.downloadLabel') }}
                      </v-btn>
                    </div>
                  </template>
                </v-col>
              </v-row>
            </div>
          </v-expand-transition>

          <v-divider></v-divider>

          <!-- Quality Control -->
          <div class="section-header d-flex justify-space-between align-center px-6 py-3" @click="toggleSection('quality')">
            <span class="section-title">{{ $t('memberDataSection.greenCoffee.qualityControl') }}</span>
            <v-icon>{{ sections.quality ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
          <v-expand-transition>
            <div v-if="sections.quality">
              <div class="px-6 pt-4 pb-2">
                <div class="d-flex flex-wrap">
                  <v-btn
                    outlined
                    color="primary"
                    class="text-none mr-2 mb-2"
                    @click.prevent="openStartCupping"
                  >
                    <v-icon left small>mdi-coffee</v-icon>
                    {{ $t('memberDataSection.greenCoffee.startCupping') }}
                  </v-btn>
                  <v-btn
                    outlined
                    color="primary"
                    class="text-none mb-2"
                    @click.prevent="openCuppingHistory"
                  >
                    <v-icon left small>mdi-history</v-icon>
                    {{ $t('memberDataSection.greenCoffee.cuppingHistory') }}
                  </v-btn>
                </div>
              </div>

              <!-- Pie chart -->
              <div v-if="pieSegments.length" class="d-flex align-center px-6 pt-4 pb-2">
                <svg width="220" height="220" viewBox="0 0 220 220" class="flex-shrink-0">
                  <path v-for="seg in pieSegments" :key="'pie-' + seg.lotId" :d="seg.path" :fill="seg.color" />
                </svg>
                <div class="ml-6">
                  <div v-for="seg in chartSegments" :key="'legend-' + seg.lotId" class="d-flex align-center mb-2">
                    <span class="legend-dot mr-2" :style="{ backgroundColor: seg.color }"></span>
                    <span class="text-body-2">{{ seg.lotId }}: {{ seg.percentage }}%</span>
                  </div>
                </div>
              </div>

              <!-- Lot cards -->
              <div v-for="(lot, idx) in detail.qualityGradings" :key="'lot-' + idx">
                <v-divider></v-divider>
                <div class="px-6 py-4">
                  <div class="lot-id mb-3">{{ $t('memberDataSection.greenCoffee.lotId') }}: {{ lot.lotId || '—' }}</div>
                  <v-row>
                    <v-col cols="12" sm="4">
                      <div class="field-label">{{ $t('memberDataSection.greenCoffee.quantity') }}</div>
                      <div class="field-value">{{ lot.quantity != null ? lot.quantity + ' kg' : '—' }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">{{ $t('memberDataSection.greenCoffee.packing') }}</div>
                      <div class="field-value">{{ lot.packing }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">{{ $t('memberDataSection.greenCoffee.totalBags') }}</div>
                      <div class="field-value">{{ lot.pocketQuantity != null ? lot.pocketQuantity : '—' }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">{{ $t('memberDataSection.greenCoffee.leftoverQuantity') }}</div>
                      <div class="field-value">{{ computeLeftover(lot) }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">{{ $t('memberDataSection.greenCoffee.qualityScore') }}</div>
                      <div class="field-value">{{ lot.qualityScore || '—' }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">{{ $t('memberDataSection.greenCoffee.label') }}</div>
                      <div class="field-value">{{ lot.label && lot.label !== '{}' ? lot.label : '—' }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">{{ $t('memberDataSection.greenCoffee.percentage') }}</div>
                      <div class="field-value">{{ lot.percentage != null ? lot.percentage + '%' : '—' }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">{{ $t('memberDataSection.greenCoffee.latestCuppingScore') }}</div>
                      <div class="field-value">
                        {{ detail.latestCuppingScore != null ? detail.latestCuppingScore : '—' }}
                        <span class="ml-1">
                          <a href="javascript:void(0)" class="link-text primary--text" @click.prevent="openCuppingHistory">{{ $t('memberDataSection.greenCoffee.cuppingHistory') }}</a>
                        </span>
                      </div>
                    </v-col>
                  </v-row>

                  <v-row class="mt-2 align-center">
                    <v-col cols="12" class="d-flex justify-space-between align-center">
                      <v-btn
                        outlined
                        color="primary"
                        class="text-none start-cupping-btn"
                        @click.prevent="openStartCupping"
                      >
                        <v-icon left small>mdi-coffee</v-icon>
                        {{ $t('memberDataSection.greenCoffee.startCupping') }}
                      </v-btn>
                    </v-col>
                  </v-row>
                  <v-row v-if="(lot.labelUrl || detail.labelUrl) || detail.qrCodeUrl" class="mt-4">
                    <v-col v-if="lot.labelUrl || detail.labelUrl" cols="12" sm="4">
                      <div class="mt-2">
                        <v-img
                          :src="lot.labelUrl || detail.labelUrl || '/img/traceabilityLabel.png'"
                          max-width="120"
                          max-height="120"
                          contain
                        ></v-img>
                        <v-btn
                          v-if="lot.labelUrl || detail.labelUrl"
                          color="brown darken-2"
                          class="white--text text-none px-6 mt-3 ml-3"
                          @click="downloadLabel(lot.labelUrl || detail.labelUrl)"
                        >
                          {{ $t('memberDataSection.greenCoffee.downloadLabel') }}
                        </v-btn>
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </div>
              <div v-if="!detail.qualityGradings || !detail.qualityGradings.length" class="px-6 py-4 text--secondary text-body-2">
                {{ $t('memberDataSection.greenCoffee.noQualityGrades') }}
              </div>
            </div>
          </v-expand-transition>

          <v-divider></v-divider>

          <!-- Waste -->
          <div class="section-header d-flex justify-space-between align-center px-6 py-3" @click="toggleSection('waste')">
            <span class="section-title">{{ $t('memberDataSection.greenCoffee.waste') }}</span>
            <v-icon>{{ sections.waste ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
          <v-expand-transition>
            <div v-if="sections.waste" class="px-6 py-4">
              <v-row>
                <v-col cols="12" sm="4">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.totalWaste') }}</div>
                  <div class="field-value">{{ effectiveTotalWaste != null ? effectiveTotalWaste + ' kg' : '—' }}</div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">{{ $t('memberDataSection.greenCoffee.percentage') }}</div>
                  <div class="field-value">{{ wastePercentage }}</div>
                </v-col>
              </v-row>
            </div>
          </v-expand-transition>
        </template>

        <div v-else-if="!loading" class="pa-10 text-center text--secondary">{{ $t('memberDataSection.greenCoffee.noDataFound') }}</div>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn color="brown darken-2" class="white--text text-uppercase" depressed @click="close">{{ $t('memberDataSection.greenCoffee.close') }}</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Start Cupping modal (single modal with full form) -->
    <StartCuppingModal
      v-model="startCuppingVisible"
      :green-coffee-id="itemId"
      @submit="onCuppingSubmit"
      @close="startCuppingVisible = false"
    />

    <!-- Cupping History modal -->
    <CuppingHistoryModal
      v-model="cuppingHistoryVisible"
      :green-coffee-id="itemId"
      :display-id="cuppingHistoryDisplayId"
      @close="cuppingHistoryVisible = false"
    />
  </v-dialog>
</template>

<script>
import moment from "moment";
import GreenCoffeeService from "@/_services/GreenCoffeeService";
import StartCuppingModal from "./StartCuppingModal.vue";
import CuppingHistoryModal from "./CuppingHistoryModal.vue";

const PIE_COLORS = ["#4caf50", "#ff9800", "#607d8b", "#2196f3", "#9c27b0", "#f44336", "#00bcd4"];

export default {
  name: "ViewGreenCoffeeModal",
  components: { StartCuppingModal, CuppingHistoryModal },
  props: {
    value: { type: Boolean, default: false },
    itemId: { type: [Number, String], default: null },
  },
  data() {
    return {
      visible: this.value,
      loading: false,
      detail: null,
      startCuppingVisible: false,
      cuppingHistoryVisible: false,
      sections: {
        parchment: true,
        process: true,
        greenBeans: true,
        traceability: true,
        quality: true,
        waste: true,
      },
    };
  },
  computed: {
    chartSegments() {
      const totalGreenBeans = Number(this.detail?.totalGreenBeans) || 0;
      const totalWaste = this.effectiveTotalWaste;
      const hasWasteValue = totalWaste != null && Number(totalWaste) > 0;
      if (!totalGreenBeans) {
        return hasWasteValue ? [{ lotId: "Waste", percentage: 100, color: "#bdbdbd" }] : [];
      }

      const segments = [];
      const lots = this.detail.qualityGradings || [];
      lots.forEach((lot, i) => {
        const qty = Number(lot.quantity) || 0;
        const pct = Math.round((qty / totalGreenBeans) * 100);
        if (pct > 0) {
          segments.push({
            lotId: lot.lotId || `Batch ${i + 1}`,
            percentage: pct,
            color: PIE_COLORS[i % PIE_COLORS.length],
          });
        }
      });

      const wasteAmount = Number(totalWaste) || 0;
      const wastePct = totalGreenBeans ? Math.round((wasteAmount / totalGreenBeans) * 100) : 0;
      if (wastePct > 0) {
        segments.push({ lotId: "Waste", percentage: wastePct, color: "#bdbdbd" });
      }

      const used = segments.reduce((s, seg) => s + seg.percentage, 0);
      if (used < 100) {
        segments.push({ lotId: "Other", percentage: 100 - used, color: "#e0e0e0" });
      }
      return segments;
    },
    pieSegments() {
      const segments = this.chartSegments.filter((s) => s.percentage > 0);
      if (!segments.length) return [];
      const cx = 110, cy = 110, r = 95;
      const paths = [];
      let currentAngle = -90;
      for (const seg of segments) {
        const sweep = (seg.percentage / 100) * 360;
        paths.push({ ...seg, path: this.describeSlice(cx, cy, r, currentAngle, currentAngle + sweep) });
        currentAngle += sweep;
      }
      return paths;
    },
    /** Waste = total green beans − sum of quality grading quantities */
    calculatedTotalWaste() {
      if (!this.detail) return null;
      const total = Number(this.detail.totalGreenBeans) || 0;
      if (!total) return null;
      const lotsSum = (this.detail.qualityGradings || []).reduce(
        (sum, lot) => sum + (Number(lot.quantity) || 0),
        0
      );
      return Math.max(0, total - lotsSum);
    },
    effectiveTotalWaste() {
      if (!this.detail) return null;
      const fromApi = this.detail.totalWaste;
      if (fromApi != null && fromApi !== "") return Number(fromApi) || 0;
      return this.calculatedTotalWaste;
    },
    wastePercentage() {
      const waste = this.effectiveTotalWaste;
      if (waste == null) return "—";
      const total = Number(this.detail?.totalGreenBeans) || 0;
      if (!total) return "—";
      return Math.round((waste / total) * 100) + " %";
    },
    cuppingHistoryDisplayId() {
      if (!this.detail) return "";
      const outturn = this.detail.outturnNumber;
      const firstLot = this.detail.qualityGradings && this.detail.qualityGradings[0];
      const lotId = firstLot && (firstLot.lotId || firstLot.greenBeansId);
      if (outturn) return `Outturn: ${outturn}`;
      if (lotId) return `ID ${lotId}`;
      return this.detail.greenBeansId ? `ID ${this.detail.greenBeansId}` : "";
    },
  },
  watch: {
    value(v) {
      this.visible = v;
      if (v && this.itemId) this.fetchDetail();
    },
    visible(v) {
      this.$emit("input", v);
    },
    itemId(v) {
      if (v && this.visible) this.fetchDetail();
    },
  },
  methods: {
    close() {
      this.visible = false;
      this.$emit("close");
    },
    toggleSection(key) {
      this.sections[key] = !this.sections[key];
    },
    formatDate(date) {
      return date ? moment(date).format("DD-MMM-YYYY").toUpperCase() : "—";
    },
    computeLeftover(lot) {
      const qty = Number(lot.quantity) || 0;
      const unit = Number(lot.unitSize) || 1;
      const bags = Math.floor(qty / unit);
      const leftover = qty - bags * unit;
      return leftover > 0 ? leftover + " kg" : "0 kg";
    },
    describeSlice(cx, cy, r, startDeg, endDeg) {
      const sweepDeg = endDeg - startDeg;
      if (sweepDeg >= 360) {
        return `M ${cx - r} ${cy} a ${r} ${r} 0 1 0 ${2 * r} 0 a ${r} ${r} 0 1 0 ${-2 * r} 0`;
      }
      const toRad = (deg) => (deg * Math.PI) / 180;
      const x1 = cx + r * Math.cos(toRad(startDeg));
      const y1 = cy + r * Math.sin(toRad(startDeg));
      const x2 = cx + r * Math.cos(toRad(endDeg));
      const y2 = cy + r * Math.sin(toRad(endDeg));
      const large = endDeg - startDeg > 180 ? 1 : 0;
      return `M ${cx} ${cy} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`;
    },
    openStartCupping() {
      this.startCuppingVisible = true;
    },
    openCuppingHistory() {
      this.cuppingHistoryVisible = true;
    },
    async onCuppingSubmit(payload) {
      try {
        await GreenCoffeeService.saveAdminCupping(payload);
        this.$emit("cupping-submitted", payload);
        this.$notify?.({ text: this.$t('memberDataSection.greenCoffee.cuppingDataSaved') || "Cupping data saved.", type: "success" });
        this.fetchDetail();
      } catch (e) {
        console.error("Failed to save cupping:", e);
        this.$notify?.({ text: e?.msg || e?.message || "Failed to save cupping.", type: "error" });
      }
    },
    async fetchDetail() {
      this.loading = true;
      this.detail = null;
      try {
        const res = await GreenCoffeeService.getAdminGreenCoffeeById(this.itemId);
        if (res?.data) this.detail = res.data;
      } catch (e) {
        console.error("Failed to load green coffee detail:", e);
      } finally {
        this.loading = false;
      }
    },
    async downloadLabel(url) {
      await this.downloadImageFile(
        url,
        `${this.getDownloadBaseName()}-label.png`,
        "Failed to download label."
      );
    },
    async downloadQr() {
      await this.downloadImageFile(
        this.detail?.qrCodeUrl,
        `${this.getDownloadBaseName()}-qr.png`,
        "Failed to download QR code."
      );
    },
    getDownloadBaseName() {
      const raw =
        this.detail?.outturnNumber ||
        this.detail?.greenBeansId ||
        (this.itemId != null ? `green-coffee-${this.itemId}` : "green-coffee");
      if (!raw || raw === "—") {
        return this.itemId != null ? `green-coffee-${this.itemId}` : "green-coffee";
      }
      const safe = String(raw).replace(/[^a-zA-Z0-9._-]+/g, "_").replace(/^_+|_+$/g, "");
      return safe || (this.itemId != null ? `green-coffee-${this.itemId}` : "green-coffee");
    },
    triggerBlobDownload(blob, filename) {
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    },
    async downloadImageFile(url, filename, errorMessage) {
      if (!url) {
        this.$notify?.({ text: errorMessage, type: "error" });
        return;
      }

      if (this.isTraceabilityPageUrl(url)) {
        this.$notify?.({
          text: "No printable label image is available for this record. Assign a traceability label template first.",
          type: "error",
        });
        return;
      }

      try {
        if (typeof url === "string" && url.startsWith("data:")) {
          const res = await fetch(url);
          const blob = await res.blob();
          if (!blob?.size) throw new Error("Empty image data");
          this.triggerBlobDownload(blob, filename);
          return;
        }

        if (typeof url === "string" && /^https?:\/\//i.test(url)) {
          await this.downloadRemoteImage(url, filename);
          return;
        }

        if (typeof url === "string" && url.startsWith("/")) {
          const res = await fetch(url, { credentials: "same-origin" });
          const blob = await res.blob();
          if (!blob?.size) throw new Error("Empty image response");
          this.triggerBlobDownload(blob, filename);
          return;
        }

        throw new Error("Unsupported image URL");
      } catch (e) {
        console.error(errorMessage, e);
        this.$notify?.({ text: errorMessage, type: "error" });
      }
    },
    isTraceabilityPageUrl(url) {
      return typeof url === "string" && /trace-your-product/i.test(url);
    },
    withCacheBust(url) {
      try {
        const parsed = new URL(url);
        parsed.searchParams.set("t", String(Date.now()));
        return parsed.toString();
      } catch (e) {
        return url;
      }
    },
    downloadRemoteImage(url, filename) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth || img.width;
          canvas.height = img.naturalHeight || img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          canvas.toBlob((blob) => {
            if (blob) {
              this.triggerBlobDownload(blob, filename);
              resolve();
            } else {
              reject(new Error("Canvas export failed"));
            }
          }, "image/png");
        };
        img.onerror = () => reject(new Error("Image load failed"));
        img.src = this.withCacheBust(url);
      });
    },
  },
};
</script>

<style scoped>
.section-header {
  cursor: pointer;
  user-select: none;
  background-color: #fafafa;
}
.section-header:hover {
  background-color: #f5f5f5;
}
.section-title {
  color: #6d4c41;
  font-weight: 700;
  font-size: 15px;
}
.lot-id {
  color: #6d4c41;
  font-weight: 700;
  font-size: 15px;
}
.field-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.54);
  line-height: 1.4;
}
.field-value {
  font-weight: 700;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
  margin-top: 2px;
}
.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}
.link-text {
  color: var(--v-primary-base);
  text-decoration: underline;
  cursor: pointer;
}
</style>